import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, CardFooter, Col, Row } from 'reactstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import FormDateInput from '../../../components/form/FormDateInput';
import FormSelect from '../../../components/form/FormSelect';
import { useDispatch } from 'react-redux';
import {
  archiveOptionsGet,
  crmsOptionGet,
  processOptionGet,
  leadRetriesReportGet,
  getListsByProcess
} from '../../../store/slices/tenantSlices/dailyReportSlice';
import LeadRetriesReportDetailsModal from './LeadRetriesReportDetailsModal';
import { endOfDay, startOfDay } from 'date-fns';
import AdminHeader from '../../../components/header/AdminHeader';
import CallexLoader from '../../../components/CallexLoader';
import FormInput from '../../../components/form/FormInput';
import {
  checkModulePermission,
  convertToMask,
  formatArchiveOptions,
  formatDownloadDate,
  formatPayloadDate,
  getDateRangeFromLabel
} from '../../../utils/common';
import { useUserRolePermission } from '../../../store/slices/authSlice';
import { useTimezone } from '../../../store/slices/commonSlice';
import { toZonedTime } from 'date-fns-tz';

const LeadRetriesReport = () => {
  const dispatch = useDispatch();
  const timezone = useTimezone();
  const userRolePermission = useUserRolePermission();
  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [crmOptions, setCrmOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [processOptions, setProcessOptions] = useState([]);
  const [reportData, setReportData] = useState([]);
  const [reportHeader, setReportHeader] = useState([]);
  const [isExporting, setIsExporting] = useState(false);
  const [isCsvDownload, setIsCsvDownload] = useState(false);
  const [csvReportData, setCsvReportData] = useState([]);
  const [csvHeaderData, setCsvHeaderData] = useState([]);
  const [archiveOptions, setArchiveOptions] = useState([]);
  const [listOptions, setListOptions] = useState([]);

  // Details state
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);

  const today = toZonedTime(new Date(), timezone);

  const initialValues = {
    crm_id: null,
    destination_number: '',
    process_id: null,
    listId: 'All',
    start_date: startOfDay(today),
    end_date: endOfDay(today),
    archiveId: 0
  };

  const validationSchema = Yup.object({
    start_date: Yup.date().required('Start date is required'),
    end_date: Yup.date().required('End date is required'),
    crm_id: Yup.number().required('CRM is required'),
    process_id: Yup.number().required('Process is required'),
    listId: Yup.string().required('List is required')
  });

  const onSubmit = async () => {
    setIsLoading(true);
    setIsFilterOpen(false);
    try {
      const formattedStartDate = formatPayloadDate(values.start_date);
      const formattedEndDate = formatPayloadDate(values.end_date);
      const payload = {
        ...values,
        destination_number: values?.destination_number || undefined,
        start_date: formattedStartDate,
        end_date: formattedEndDate
      };

      const response = await dispatch(leadRetriesReportGet(payload)).unwrap();
      if (response) {
        setReportData(response?.data || []);
        setReportHeader(response?.header || []);
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log('error', error);
    }
  };

  const { values, handleChange, handleBlur, setFieldValue, handleSubmit, touched, errors } =
    useFormik({
      initialValues,
      validationSchema,
      onSubmit
    });

  useEffect(() => {
    if (values?.process_id) {
      onSubmit();
    }
  }, []);

  const getCrmsOption = async () => {
    try {
      const response = await dispatch(crmsOptionGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: val.id
        }));
        setCrmOptions(options);
      } else {
        setCrmOptions([]);
      }
    } catch (error) {
      setCrmOptions([]);
      console.log('error', error);
    }
  };

  const getProcessOption = async (id) => {
    try {
      const response = await dispatch(processOptionGet({ crm_id: id })).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: val.id
        }));
        setProcessOptions(options);
      } else {
        setProcessOptions([]);
      }
    } catch (error) {
      setProcessOptions([]);
      console.log('error', error);
    }
  };

  const getArchiveOptions = async () => {
    try {
      const response = await dispatch(archiveOptionsGet()).unwrap();
      if (response && response.data) {
        const options = formatArchiveOptions(response.data, timezone);
        setArchiveOptions(options);
      } else {
        setArchiveOptions([]);
      }
    } catch (error) {
      setArchiveOptions([]);
      console.log('error', error);
    }
  };

  const getListOption = async (id) => {
    try {
      const response = await dispatch(getListsByProcess({ process_id: id })).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.list_name,
          value: val.id
        }));
        setListOptions([{ label: 'All', value: 'All' }, ...options]);
      } else {
        setListOptions([{ label: 'All', value: 'All' }]);
      }
    } catch (error) {
      setListOptions([{ label: 'All', value: 'All' }]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    getArchiveOptions();
    getCrmsOption();
  }, []);

  useEffect(() => {
    if (values.crm_id) {
      getProcessOption(values.crm_id);
    } else {
      setProcessOptions([]);
    }
    setFieldValue('process_id', null);
    setFieldValue('listId', 'All');
    setListOptions([]);
  }, [values.crm_id]);

  useEffect(() => {
    if (values.process_id) {
      getListOption(values.process_id);
    } else {
      setListOptions([]);
    }
    setFieldValue('listId', 'All');
  }, [values.process_id]);

  const handleFetchDetails = async (row) => {
    setSelectedLead(row);
    setIsDetailsOpen(true);
  };

  const prepareExportProps = () => {
    return {
      headers: csvHeaderData,
      data: csvReportData,
      filename: `Lead_Retries_Report_${formatDownloadDate(values.start_date)}.csv`
    };
  };

  const handleExportCSV = async () => {
    setIsExporting(true);
    try {
      const formattedStartDate = formatPayloadDate(values.start_date);
      const formattedEndDate = formatPayloadDate(values.end_date);
      const payload = {
        ...values,
        start_date: formattedStartDate,
        end_date: formattedEndDate,
      };

      const response = await dispatch(leadRetriesReportGet(payload)).unwrap();
      if (response) {
        const headers = (response.header || []).map((val) => ({ label: val.label, key: val.key }));
        setCsvHeaderData(headers);
        setCsvReportData(response.data || []);
        setIsExporting(false);
        setIsCsvDownload(true);
      }
    } catch (error) {
      setIsExporting(false);
      console.log('Error exporting CSV:', error);
    }
  };

  const selectedOption = useMemo(
    () => archiveOptions.find((opt) => opt.value === values.archiveId),
    [values.archiveId, archiveOptions]
  );

  const selectedLabel = selectedOption?.label || null;

  const { minDate, maxDate } = useMemo(() => {
    return getDateRangeFromLabel(selectedLabel, timezone);
  }, [selectedLabel, timezone]);

  useEffect(() => {
    if (minDate && maxDate) {
      setFieldValue('start_date', minDate);
      setFieldValue('end_date', maxDate);
    }
  }, [minDate?.getTime(), maxDate?.getTime()]);

  return (
    <div>
      <AdminHeader
        title="Lead Retries Report"
        isSearch={true}
        isExportDisabled={!checkModulePermission('is_export', 'Lead Retries Report', userRolePermission)}
        onSearchClick={() => setIsFilterOpen(!isFilterOpen)}
        isExport={values?.crm_id && values?.process_id && reportData?.length > 0}
        csvProps={prepareExportProps()}
        onExportClick={() => handleExportCSV()}
        setIsCsvDownload={setIsCsvDownload}
        isCsvDownload={isCsvDownload}
        isExporting={isExporting}
      />
      {isFilterOpen && (
        <Card className="mb-3 p-3">
          <form onSubmit={handleSubmit}>
            <Row>
              <Col lg="3" md="3" className="flex-row">
                <FormSelect
                  label="Month"
                  placeholder="Select month"
                  value={values.archiveId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="archiveId"
                  options={archiveOptions}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3" md="3" className="flex-row">
                <FormDateInput
                  name="start_date"
                  dateFormat="yyyy-MM-dd HH:mm:ss"
                  label="From Date"
                  showTimeSelect={true}
                  value={values.start_date}
                  minDate={minDate}
                  maxDate={values.end_date || maxDate}
                  onChange={(e) => setFieldValue('start_date', e.target.value)}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3" md="3" className="flex-row">
                <FormDateInput
                  name="end_date"
                  label="To Date"
                  dateFormat="yyyy-MM-dd HH:mm:ss"
                  showTimeSelect={true}
                  minDate={values.start_date || minDate}
                  maxDate={maxDate}
                  value={values.end_date}
                  onChange={(e) => setFieldValue('end_date', e.target.value)}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3" md="3" className="flex-row">
                <FormInput
                  label="Destination Number"
                  placeholder="Enter Destination Number"
                  value={values.destination_number}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="destination_number"
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3" md="3" className="flex-row">
                <FormSelect
                  label="CRM"
                  placeholder="Select CRM"
                  value={values.crm_id}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="crm_id"
                  options={crmOptions}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3" md="3" className="flex-row">
                <FormSelect
                  label="Process"
                  placeholder="Select Process"
                  value={values.process_id}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="process_id"
                  options={processOptions}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3" md="3" className="flex-row">
                <FormSelect
                  label="List"
                  placeholder="Select List"
                  value={values.listId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="listId"
                  options={listOptions}
                  touched={touched}
                  errors={errors}
                />
              </Col>
            </Row>
            <CardFooter className="p-0 bg-white text-end">
              <Button color="primary" className="px-4" type="submit">
                Search
              </Button>
            </CardFooter>
          </form>
        </Card>
      )}

      {isLoading ? (
        <Card className="p-0 d-flex justify-content-center align-items-center m-0" style={{ height: isFilterOpen ? 'calc(100vh - 410px)' : 'calc(100vh - 170px)' }}>
          <CallexLoader />
        </Card>
      ) : reportData?.length > 0 ? (
        <Card className="p-0 position-relative m-0">
          <div className="daily-report-table" style={{ height: isFilterOpen ? 'calc(100vh - 410px)' : 'calc(100vh - 170px)' }}>
            <table className="report-list-table">
              <thead>
                <tr>
                  {reportHeader?.map((val, index) => (
                    <th key={index}>{val.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {reportData?.map((val, index) => (
                  <tr key={index}>
                    {reportHeader?.map((header, idx) => {
                      if (header.key === 'count') {
                        return (
                          <td key={idx} className="text-primary cursor-pointer text-decoration-underline" onClick={() => handleFetchDetails(val)}>
                            {val[header.key]}
                          </td>
                        );
                      }
                      if (header.key === 'destination_number') {
                        return (
                          <td key={idx}>
                            {process.env.REACT_APP_GLOBAL_NUMBER_MASKING === 'true'
                              ? convertToMask(val[header.key] || '-')
                              : val[header.key] || '-'}
                          </td>
                        );
                      }
                      return <td key={idx}>{val[header.key] || '-'}</td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : (
        <Card className="p-0 d-flex justify-content-center align-items-center no-data m-0" style={{ height: isFilterOpen ? 'calc(100vh - 410px)' : 'calc(100vh - 170px)' }}>
          No data available
        </Card>
      )}

      {/* Details Modal */}
      <LeadRetriesReportDetailsModal
        isOpen={isDetailsOpen}
        onCloseModal={() => setIsDetailsOpen(false)}
        selectedLead={selectedLead}
        filters={values}
      />
    </div>
  );
};

export default LeadRetriesReport;
